<template>
  <TirPmPopover
    ref="$popover"
    :class="['picker-container-popover', `input-type--${type}`]"
    classes="picker-container"
    :position="popover.position"
    :custom-position="popover.customPosition"
    :is-disabled="isDisabled"
    :is-no-teleport="popover.isNoTeleport"
    data-picker-container-popover
    @open="onOpen"
    @close="onClose"
  >
    <div class="picker-container__target">
      <slot :open="open" :close="close" :toggle="toggle" />
    </div>
    <template #content>
      <div class="picker-container__content" ref="$content" @click.stop>
        <slot name="content" />
      </div>
    </template>
  </TirPmPopover>
</template>

<script lang="ts" setup>
import { nextTick, PropType, ref, watch } from "vue";
import { TirPmPopover } from "tir-pm-popover";
import { DatepickerTypeEnum, PickerGridTypeEnum } from "../../enums";
import { DatepickerPopoverProps } from "../../types";

const emit = defineEmits<{
  /**Событие открытия */
  (e: "open"): void;
  /**Событие закрытия */
  (e: "close"): void;
}>();

const props = defineProps({
  /**Отображаемая сетка */
  gridType: {
    type: Number as PropType<PickerGridTypeEnum>,
    required: true,
  },
  /**Тип пикера */
  type: {
    type: String as PropType<DatepickerTypeEnum>,
    required: true,
  },
  /**Блокировка */
  isDisabled: { type: Boolean, default: false },
  /**Настройки поповера */
  popover: {
    type: Object as PropType<DatepickerPopoverProps>,
    required: true,
  },
});

/**Ссылка на компонент поповера */
const $popover = ref<InstanceType<typeof TirPmPopover>>();
/**Флаг открыт ли поповер */
const isOpened = ref(false);

/**При открытии */
const onOpen = () => {
  isOpened.value = true;
  emit("open");
};
/**При закрытии */
const onClose = () => {
  isOpened.value = false;
  emit("close");
};
/**Открыть */
const open = () => $popover.value?.open();
/**Закрыть */
const close = () => $popover.value?.close();
/**Открыть/закрыть */
const toggle = () => {
  if (isOpened.value) close();
  else open();
};

/**Обновление позиционирования поповера при смене сетки */
watch(
  () => props.gridType,
  async () => {
    await nextTick();
    $popover.value?.init(true);
  }
);

defineExpose({
  open,
  close,
  toggle,
});
</script>

<style lang="scss" scoped>
.picker-container {
  &__content {
    background-color: var(--tir-pm-datepicker__container-bg);
    border-radius: var(--tir-pm-datepicker__container-br);
    border: var(--tir-pm-datepicker__container-border);
    box-shadow: var(--tir-pm-datepicker__container-shadow);
    padding: var(--tir-pm-datepicker__container-padding);
  }
}
</style>

<style lang="scss">
.tir-popover_element.picker-container-popover[data-picker-container-popover] {
  display: block;
  width: unset;
  &.input-type {
    &--date {
      max-width: var(--tir-pm-datepicker__input-date-max-width);
      min-width: var(--tir-pm-datepicker__input-date-min-width);
    }
    &--date-time {
      max-width: var(--tir-pm-datepicker__input-datetime-max-width);
      min-width: var(--tir-pm-datepicker__input-datetime-min-width);
    }
    &--week {
      max-width: var(--tir-pm-datepicker__input-week-max-width);
      min-width: var(--tir-pm-datepicker__input-week-min-width);
    }
    &--month {
      max-width: var(--tir-pm-datepicker__input-month-max-width);
      min-width: var(--tir-pm-datepicker__input-month-min-width);
    }
    &--year {
      max-width: var(--tir-pm-datepicker__input-year-max-width);
      min-width: var(--tir-pm-datepicker__input-year-min-width);
    }
    &--time {
      max-width: var(--tir-pm-datepicker__input-time-max-width);
      min-width: var(--tir-pm-datepicker__input-time-min-width);
    }
  }
}
</style>
